import { useNavigate, useSearch } from '@tanstack/react-router';
import { z } from 'zod';

export const CREATIVE_DETAIL_TABS = ['overview', 'scores', 'tags'] as const;

export type CreativeDetailTab = (typeof CREATIVE_DETAIL_TABS)[number];
export type CreativeDetailSearch = { openCreativeId?: string; tab?: CreativeDetailTab };

const creativeDetailSearchSchema = z
  .object({
    openCreativeId: z.string().optional().catch(undefined),
    tab: z.enum(CREATIVE_DETAIL_TABS).optional().catch(undefined),
  })
  .catch({});

export function validateCreativeDetailSearch(
  search: Record<string, unknown>,
): CreativeDetailSearch {
  const parsed = creativeDetailSearchSchema.parse(search);
  const result: CreativeDetailSearch = {};
  if (parsed.openCreativeId !== undefined) result.openCreativeId = parsed.openCreativeId;
  if (parsed.tab !== undefined) result.tab = parsed.tab;
  return result;
}

export function useCreativeDetailSearch(to: '/explore'): {
  creativeId: string | null;
  tab: CreativeDetailTab;
  open: (creativeId: string) => void;
  close: () => void;
  setTab: (tab: CreativeDetailTab) => void;
} {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as CreativeDetailSearch;

  function patch(next: CreativeDetailSearch): void {
    void navigate({
      to,
      replace: true,
      search: (previous) => {
        const base = { ...(previous as Record<string, unknown>) };
        delete base['openCreativeId'];
        delete base['tab'];
        return { ...base, ...next };
      },
    });
  }

  return {
    creativeId: search.openCreativeId ?? null,
    tab: search.tab ?? 'overview',
    open: (creativeId: string) => patch({ openCreativeId: creativeId, tab: 'overview' }),
    close: () => patch({}),
    setTab: (tab: CreativeDetailTab) =>
      patch({ ...(search.openCreativeId ? { openCreativeId: search.openCreativeId } : {}), tab }),
  };
}
